import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiInfo, FiMinusCircle, FiPlusCircle } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Pagination from "../../../helper/Pagination";
import { MdDelete, MdModeEditOutline, MdClose } from "react-icons/md";
import { FaSearch } from "react-icons/fa";
import { fetchRevenueRequest } from "../../redux/actions/revenueActions";
import Select from "react-select";
import { getOemNames } from "../../redux/actions/oemActions";
import { FaDollarSign, FaYenSign, FaChartLine, FaCoins, FaMoneyBillWave } from "react-icons/fa";
import "../Coupon/AllCoupon.css";

const Revenue = () => {
  const dispatch = useDispatch();
  const {
    data: revenueData,
    loading,
    pagination,
    revenue,
  } = useSelector((state) => state.revenue);
  const [showInputForm, setShowInputForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [productTypeSearch, setProductTypeSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [oemFilter, setOemFilter] = useState(null);
  const { oemNames } = useSelector((state) => state.oem);
  const isInitialMount = useRef(true);

  const clearDateRange = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const fetchRevenueData = () => {
    const params = {
      page: currentPage,
      limit: pagination?.itemsPerPage || 10,
    };
    if (oemFilter?.value) params.oemId = oemFilter.value;
    if (startDate) {
      const year = startDate.getFullYear();
      const month = String(startDate.getMonth() + 1).padStart(2, '0');
      const day = String(startDate.getDate()).padStart(2, '0');
      params.startDate = `${year}-${month}-${day}`;
    }
    if (endDate) {
      const adjustedEndDate = new Date(endDate);
      adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
      const year = adjustedEndDate.getFullYear();
      const month = String(adjustedEndDate.getMonth() + 1).padStart(2, '0');
      const day = String(adjustedEndDate.getDate()).padStart(2, '0');
      params.endDate = `${year}-${month}-${day}`;
    }
    if (searchTerm) params.search = searchTerm;
    dispatch(fetchRevenueRequest(params));
  };

  useEffect(() => {
    dispatch(getOemNames());
  }, [dispatch]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (currentPage === 1) {
        fetchRevenueData();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, startDate, endDate, oemFilter]);

  useEffect(() => {
    fetchRevenueData();
  }, [currentPage]);

  return (
    <>
      <div className="page-container">
        <div className="page-card scroll-container">
          <div className="form-header-AllCoupon d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1">
                <FaDollarSign className="me-2 text-primary" />
                Revenue Management
              </h5>
              <p className="text-muted mb-0">
                Monitor and track your revenue streams
              </p>
            </div>
            <div className="d-flex gap-3">
              <div className="stats-card">
                <div className="stats-icon bg-success">
                  <FaDollarSign />
                </div>
                <div>
                  <div className="stats-number">${revenue?.usd?.toLocaleString() || 0}</div>
                  <div className="stats-label">USD Revenue</div>
                </div>
              </div>
              <div className="stats-card">
                <div className="stats-icon bg-primary">
                  <FaYenSign />
                </div>
                <div>
                  <div className="stats-number">¥{revenue?.jpy?.toLocaleString() || 0}</div>
                  <div className="stats-label">JPY Revenue</div>
                </div>
              </div>
              <div className="stats-card">
                <div className="stats-icon bg-warning">
                  <FaCoins />
                </div>
                <div>
                  <div className="stats-number">S${revenue?.sgd?.toLocaleString() || 0}</div>
                  <div className="stats-label">SGD Revenue</div>
                </div>
              </div>
              <div className="stats-card">
                <div className="stats-icon bg-info">
                  <FaMoneyBillWave />
                </div>
                <div>
                  <div className="stats-number">HK${revenue?.hkd?.toLocaleString() || 0}</div>
                  <div className="stats-label">HKD Revenue</div>
                </div>
              </div>
              <div className="stats-card">
                <div className="stats-icon bg-secondary">
                  <FaDollarSign />
                </div>
                <div>
                  <div className="stats-number">A${revenue?.aud?.toLocaleString() || 0}</div>
                  <div className="stats-label">AUD Revenue</div>
                </div>
              </div>
            </div>
          </div>
          <div className="table-section">
            <div className="d-flex justify-content-end align-items-center mb-1">
              {/* <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0">Revenue List :</h6>
                <span className="text-dark">{revenueData.length} items</span>
              </div> */}
              <div className="position-relative d-flex justify-content-end mb-1 gap-3">
                <div className="position-relative">
                  <input
                    type="search"
                    placeholder="Search By Name & Email..."
                    className="form-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value.trimStart())}
                    style={{ width: "200px" }}
                  />
                  {searchTerm === "" && (
                    <div
                      className="position-absolute"
                      style={{
                        top: "50%",
                        right: "13px",
                        transform: "translateY(-50%)",
                        color: "#666",
                      }}
                    >
                      <FaSearch />
                    </div>
                  )}
                </div>
                <Select
                  value={oemFilter}
                  onChange={setOemFilter}
                  options={oemNames?.map((oem) => ({
                    value: oem._id,
                    label: oem.companyName,
                  }))}
                  placeholder="All OEM"
                  isClearable
                  isSearchable
                  classNamePrefix="react-select"
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      width: "200px",
                    }),
                    control: (base) => ({
                      ...base,
                      minHeight: "30px",
                      height: "30px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      "&:hover": {
                        border: "1px solid #ddd",
                      },
                    }),
                  }}
                />
                <div className="position-relative">
                  <DatePicker
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => {
                      const [start, end] = update;
                      setStartDate(start);
                      setEndDate(end);
                    }}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    placeholderText="Select date range"
                    popperProps={{ strategy: "fixed" }}
                    popperPlacement="bottom"
                    popperClassName="date-picker-popper"
                    wrapperClassName="date-picker-wrapper"
                    wrapperStyle={{
                      width: "100%",
                      height: "30px",
                    }}
                    calendarClassName="border-0 shadow"
                    customInput={
                      <input
                        style={{
                          height: "30px",
                          padding: "0.375rem 0.75rem",
                          paddingRight: "15px",
                          cursor: "pointer",
                          fontSize: "12px",
                          width: "200px",
                        }}
                      />
                    }
                  />
                  {(startDate || endDate) && (
                    <MdClose
                      className="position-absolute"
                      style={{
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#666",
                        zIndex: 1001,
                      }}
                      onClick={clearDateRange}
                      title="Clear date range"
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="table-scroll-wrapper">
              <table className="partners-table">
                <thead>
                  <tr>
                    <th>Sr No.</th>
                    <th>User Name</th>
                    <th>Email</th>
                    <th>Amount</th>
                    <th>Currency</th>
                    <th>Paid At</th>
                    {/* <th>Actions</th> */}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="7"
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#666",
                        }}
                      >
                        <div className="loading-spinner">
                          <div className="spinner"></div>
                          <p>Loading...</p>
                        </div>
                      </td>
                    </tr>
                  ) : revenueData.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#666",
                        }}
                      >
                        No revenue data available
                      </td>
                    </tr>
                  ) : (
                    revenueData.map((item, index) => (
                        <tr key={item._id}>
                          <td>{(currentPage - 1) * pagination.itemsPerPage + index + 1}</td>
                          <td>{item.user?.firstName} {item.user?.lastName}</td>
                          <td>{item.user?.email || "N/A"}</td>
                          <td>{item.currency === "jpy" ? '¥' : '$'}{item.amount !== undefined ? item.amount : "N/A"}</td>
                          <td>{item.currency?.toUpperCase() || "N/A"}</td>
                          <td>{new Date(item.createdAt).toLocaleDateString('en-GB')}</td>
                          {/* <td>
                            <div className="row-actions">
                              <div className="edit-row-btn" title="Edit">
                                <MdModeEditOutline />
                              </div>
                              <div className="delete-row-btn" title="Delete">
                                <MdDelete />
                              </div>
                            </div>
                          </td> */}
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Revenue;
